import React, { ReactNode, useState } from "react";
import {
  ArrowPathIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  EyeIcon,
  EyeSlashIcon,
  ExclamationCircleIcon,
  MinusCircleIcon,
} from "@heroicons/react/24/outline";
import { classNames } from "components/utils";

type StatusTone = "neutral" | "positive" | "attention" | "loading";

interface ExpandableConfigurationSectionProps {
  id: string;
  title: string;
  summary: ReactNode;
  collapsedDetails?: Array<{
    label: string;
    value: string;
    isUnset?: boolean;
    masked?: boolean;
    required?: boolean;
  }>;
  compactCollapsedDetails?: boolean;
  summaryWarning?: ReactNode;
  summaryWarningTone?: "warning" | "error";
  status?: string;
  statusTone?: StatusTone;
  actionLabel?: string;
  disabled?: boolean;
  open: boolean;
  onToggle: () => void;
  children: ReactNode;
}

const statusToneClasses: Record<StatusTone, string> = {
  neutral: "border-zinc-700 bg-zinc-800/70 text-secondary",
  positive: "border-highlight bg-highlight/10 text-highlight",
  attention: "border-amber-900/80 bg-amber-950/40 text-amber-300",
  loading: "border-zinc-700 bg-zinc-800/70 text-secondary",
};

const statusIcons: Record<StatusTone, React.ElementType> = {
  neutral: MinusCircleIcon,
  positive: CheckCircleIcon,
  attention: ExclamationCircleIcon,
  loading: ArrowPathIcon,
};

export const ExpandableConfigurationSection: React.FC<
  ExpandableConfigurationSectionProps
> = ({
  id,
  title,
  summary,
  collapsedDetails,
  compactCollapsedDetails = false,
  summaryWarning,
  summaryWarningTone = "warning",
  status,
  statusTone = "neutral",
  actionLabel = "Change",
  disabled = false,
  open,
  onToggle,
  children,
}) => {
  const contentId = `${id}-content`;
  const StatusIcon = statusIcons[statusTone];
  const [revealedValues, setRevealedValues] = useState<Set<string>>(
    () => new Set(),
  );

  const toggleRevealedValue = (label: string) => {
    setRevealedValues((current) => {
      const next = new Set(current);
      if (next.has(label)) {
        next.delete(label);
      } else {
        next.add(label);
      }
      return next;
    });
  };

  return (
    <section className="border-t border-zinc-800">
      <div
        className={classNames(
          "group flex w-full items-center gap-3 px-5 py-3.5 text-left transition-colors",
          disabled ? "cursor-default" : "cursor-pointer hover:bg-zinc-900/50",
        )}
        onClick={(event) => {
          if (disabled || (event.target as HTMLElement).closest("button"))
            return;
          onToggle();
        }}
      >
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="text-sm font-medium text-zinc-200">{title}</h3>
              {status && (
                <span
                  className={classNames(
                    "inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] font-medium leading-none",
                    statusToneClasses[statusTone],
                  )}
                >
                  <StatusIcon
                    className={classNames(
                      "h-3 w-3",
                      statusTone === "loading" ? "animate-spin" : "",
                    )}
                    aria-hidden="true"
                  />
                  {status}
                </span>
              )}
            </div>
            {!open &&
              (collapsedDetails && collapsedDetails.length > 0 ? (
                <dl className="flex min-w-0 flex-wrap gap-1.5">
                  {collapsedDetails.map(
                    ({ label, value, isUnset, masked, required }) => {
                      const isRevealed = revealedValues.has(label);
                      const shouldMask = masked && !isUnset && !isRevealed;
                      const VisibilityIcon = isRevealed
                        ? EyeSlashIcon
                        : EyeIcon;

                      return (
                        <div
                          key={label}
                          title={shouldMask ? undefined : value}
                          className={classNames(
                            "inline-flex w-fit max-w-full min-w-0 items-stretch overflow-hidden border",
                            compactCollapsedDetails
                              ? "rounded border-zinc-700 bg-zinc-800/70 text-[11px] leading-none text-white"
                              : "rounded-md border-zinc-700/80 bg-zinc-950/80 text-[13px] shadow-sm ring-1 ring-inset ring-white/[0.03]",
                          )}
                        >
                          <dt
                            className={classNames(
                              "min-w-0 break-words font-mono",
                              compactCollapsedDetails
                                ? "flex items-center px-1.5 py-0.5 font-medium text-white"
                                : "px-2 py-1 font-normal text-zinc-300",
                            )}
                          >
                            {label}
                            {required && isUnset && (
                              <span
                                className="ml-0.5 text-red-500"
                                aria-label={`${label} is required`}
                              >
                                *
                              </span>
                            )}
                          </dt>
                          <dd
                            className={classNames(
                              "flex min-w-0 items-center border-l border-zinc-700",
                              compactCollapsedDetails
                                ? "gap-1 px-1.5 py-0.5"
                                : "gap-1.5 px-2 py-1",
                              isUnset
                                ? compactCollapsedDetails
                                  ? "italic text-zinc-400"
                                  : "bg-zinc-900/70 italic text-zinc-500"
                                : compactCollapsedDetails
                                  ? "font-medium text-highlight"
                                  : "bg-highlight/[0.06] font-medium text-highlightLighter",
                            )}
                          >
                            <span
                              className={classNames(
                                "min-w-0 truncate",
                                shouldMask
                                  ? "font-mono tracking-wider text-zinc-400"
                                  : "",
                              )}
                            >
                              {shouldMask ? "••••••••" : value}
                            </span>
                            {masked && !isUnset && (
                              <button
                                type="button"
                                className="shrink-0 rounded-sm text-zinc-400 outline-none transition-colors hover:text-white focus-visible:ring-2 focus-visible:ring-highlight"
                                aria-label={`${isRevealed ? "Hide" : "Show"} value for ${label}`}
                                onClick={() => toggleRevealedValue(label)}
                              >
                                <VisibilityIcon
                                  className={
                                    compactCollapsedDetails
                                      ? "h-3 w-3"
                                      : "h-3.5 w-3.5"
                                  }
                                  aria-hidden="true"
                                />
                              </button>
                            )}
                          </dd>
                        </div>
                      );
                    },
                  )}
                </dl>
              ) : (
                <p className="text-sm font-normal leading-5 text-zinc-300">
                  {summary}
                </p>
              ))}
          </div>
          {summaryWarning &&
            (typeof summaryWarning === "string" ? (
              <p
                className={classNames(
                  "mt-1 text-xs leading-5",
                  summaryWarningTone === "error"
                    ? "text-red-500"
                    : "text-yellow-500",
                )}
              >
                {summaryWarning}
              </p>
            ) : (
              <div className="mt-1 text-xs leading-5">{summaryWarning}</div>
            ))}
        </div>
        <button
          type="button"
          className="ml-auto flex shrink-0 self-center items-center gap-3 rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-highlight"
          aria-expanded={open}
          aria-controls={contentId}
          onClick={onToggle}
          disabled={disabled}
        >
          <span
            className={classNames(
              "text-xs font-semibold",
              disabled
                ? "text-zinc-500"
                : "text-highlight group-hover:text-highlightLighter",
            )}
          >
            {open ? "Done" : actionLabel}
          </span>
          <ChevronDownIcon
            className={classNames(
              "h-4 w-4 shrink-0 text-zinc-500 transition-transform",
              open ? "rotate-180" : "",
            )}
            aria-hidden="true"
          />
        </button>
      </div>
      {open && (
        <div id={contentId} className="border-t border-zinc-800 bg-black/10">
          {children}
        </div>
      )}
    </section>
  );
};
